// API Endpoints Configuration
// This file centralizes all API endpoint URLs for easy maintenance

import { isLocalDevMode, getDevAuthHeader } from '../dev-auth-bypass'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const SUPABASE_FUNCTIONS_URL = `${SUPABASE_URL}/functions/v1`;

// Detect if we're in local development mode
const IS_LOCAL_DEV = typeof window !== 'undefined' 
  ? window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  : process.env.NODE_ENV === 'development';

// Use environment variable to determine if we should use Edge Functions
// Default to false for local development, true for production
const USE_EDGE_FUNCTIONS = import.meta.env.VITE_USE_EDGE_FUNCTIONS === 'true' || 
                          process.env.NEXT_PUBLIC_USE_EDGE_FUNCTIONS === 'true' ||
                          (!IS_LOCAL_DEV && import.meta.env.VITE_USE_EDGE_FUNCTIONS !== 'false');

// For local dev, check if edge functions are actually running
if (IS_LOCAL_DEV && USE_EDGE_FUNCTIONS) {
  console.log('⚠️ Edge Functions enabled in local dev - ensure Supabase Edge Runtime is running!');
}

// Only log API config once per page load to reduce spam
if (typeof window !== 'undefined' && !(window as any).apiConfigLogged) {
  console.log('🔧 API Configuration:', {
    IS_LOCAL_DEV,
    USE_EDGE_FUNCTIONS,
    hostname: window.location.hostname
  });
  (window as any).apiConfigLogged = true;
}

export const API_ENDPOINTS = {
  auth: {
    signin: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/auth-signin` : '/api/auth/signin',
    signout: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/auth-signout` : '/api/auth/signout',
    session: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/auth-session` : '/api/auth/session',
    // Updated to use Supabase auth-signin for magic links
    magicLinkRequest: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/auth-signin` : '/api/auth/magic-link/request',
    adminSignin: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/auth-signin` : '/api/admin/signin'
  },
  admin: {
    clients: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-clients` : '/api/clients',
    clientDetails: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-client-details` : '/api/admin/client',
    clientBookings: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-client-bookings` : '/api/admin/client-bookings',
    clientPayments: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-client-payments` : '/api/admin/client-payments',
    bookings: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-bookings` : '/api/admin/bookings',
    content: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-content` : '/api/admin/content',
    softwareWaitlist: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/admin-software-waitlist` : '/api/admin/software-waitlist'
  },
  debug: {
    envCheck: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/debug-env-check` : '/api/debug/env-check'
  },
  client: {
    me: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/client-me` : '/api/client/me',
    bookings: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/client-bookings` : '/api/client/bookings',
    eligibility: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/client-eligibility` : '/api/client/eligibility'
  },
  clients: {
    createOrGet: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/clients-create-or-get` : '/api/clients/create-or-get',
    saveIntakeData: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/clients-save-intake-data` : '/api/clients/save-intake-data',
    submitIntake: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/clients-submit-intake` : '/api/clients/submit-intake'
  },
  business: {
    appointmentTypes: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/appointment-types` : '/api/appointment-types',
    availability: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/availability` : '/api/availability',
    publicBookSession: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/public-book-session` : '/api/public-book-session',
    verifySession: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/verify-session` : '/api/verify-session',
    createCheckoutSession: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/create-checkout-session` : '/api/create-checkout-session',
    stripeWebhook: USE_EDGE_FUNCTIONS ? `${SUPABASE_FUNCTIONS_URL}/stripe-webhook` : '/api/stripe-webhook'
  }
};

// Configuration for which endpoints require authentication
export const ENDPOINT_AUTH_CONFIG = {
  // Public endpoints that don't require authentication
  public: [
    'clients-create-or-get',
    'auth-signin',
    'auth-signup', 
    'public-book-session',
    'appointment-types',
    'availability',
    'debug-env-check'
  ],
  // Endpoints that require user authentication (client or admin)
  authenticated: [
    'client-me',
    'client-bookings',
    'client-eligibility',
    'client-content-library',
    'clients-save-intake-data',
    'clients-submit-intake',
    'verify-session',
    'auth-session',
    'auth-signout'
  ],
  // Endpoints that require admin authentication
  admin: [
    'admin-clients',
    'admin-client-details', 
    'admin-client-bookings',
    'admin-client-payments',
    'admin-bookings',
    'admin-content',
    'admin-analytics',
    'admin-software-waitlist'
  ]
};

// Helper to determine auth requirement for an endpoint
export function getEndpointAuthType(url: string): 'public' | 'authenticated' | 'admin' {
  // Extract endpoint name from URL
  const endpointName = url.split('/').pop()?.split('?')[0] || '';
  
  if (ENDPOINT_AUTH_CONFIG.public.includes(endpointName)) {
    return 'public';
  }
  if (ENDPOINT_AUTH_CONFIG.admin.includes(endpointName)) {
    return 'admin';
  }
  if (ENDPOINT_AUTH_CONFIG.authenticated.includes(endpointName)) {
    return 'authenticated';
  }
  
  // Default to authenticated for safety
  return 'authenticated';
}

// Helper function to get auth headers for Edge Functions
export function getAuthHeaders(url?: string, includeContentType = true): HeadersInit {
  const headers: HeadersInit = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (USE_EDGE_FUNCTIONS && url) {
    const authType = getEndpointAuthType(url);
    
    // Add dev mode headers for local development
    if (IS_LOCAL_DEV) {
      headers['X-Dev-Mode'] = 'true';
    }
    
    // Public endpoints don't need auth
    if (authType === 'public') {
      // Still send anon key for Supabase edge functions
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
      if (anonKey) {
        headers['Authorization'] = `Bearer ${anonKey}`;
      }
      return headers;
    }
    
    // For authenticated/admin endpoints, try dev auth first
    const devAuthHeader = getDevAuthHeader();
    if (devAuthHeader) {
      headers['Authorization'] = devAuthHeader;
      headers['X-Dev-Mode'] = 'true';
      // Always log for debugging CORS issues
      console.log('🔧 Using dev auth header for API request to:', url);
      console.log('🔧 Dev auth header:', devAuthHeader.substring(0, 30) + '...');
    } else {
      console.log('⚠️ No dev auth header available for:', url);
      // Fall back to anon key
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
      if (anonKey) {
        headers['Authorization'] = `Bearer ${anonKey}`;
      }
    }
  }

  return headers;
}

// Helper to determine if we need credentials for the request
export function shouldIncludeCredentials(url: string): boolean {
  // Always include credentials for local API calls
  if (url.startsWith('/api/')) {
    return true;
  }
  // For Edge Functions, we handle auth via headers and cookies
  return USE_EDGE_FUNCTIONS;
}